import { generateObjectId } from '@builttocreate/engine-utils';
import { PRICING_COLUMN_CELL_DEFAULT_ROW_VALUES, PRICING_COLUMN_TYPES } from '../../../../constants/FieldPricingTableColumnTypes';
import {
  getValueType,
  parseDisplayValue,
  formatCellValue,
  getDefaultValueForColumnType,
  applyFormatChange,
  calculateRowSubtotal,
  calculateSubtotal,
  calculateTotal
} from './pricingHelpers';

/**
 * Updates all SUBTOTAL and TOTAL summary columns with calculated values
 *
 * @param {Array} columns - The regular table columns
 * @param {Array} summaryColumns - The summary columns to update
 * @returns {Array} Updated summary columns with recalculated SUBTOTAL and TOTAL values
 */
export const updateSummaryCalculatedValues = (columns, summaryColumns) => {
  if (!summaryColumns || !summaryColumns.length) return summaryColumns;

  const updatedSummaryColumns = [...summaryColumns];

  const subtotal = calculateSubtotal(columns);

  updatedSummaryColumns.forEach((column, index) => {
    if (column.type === PRICING_COLUMN_TYPES.SUBTOTAL) {
      const valueType = getValueType(column);
      updatedSummaryColumns[index] = {
        ...column,
        value: formatCellValue(subtotal, valueType),
      };
    }
  });

  // Calculate and update TOTAL columns
  const total = calculateTotal(subtotal, updatedSummaryColumns);
  updatedSummaryColumns.forEach((column, index) => {
    if (column.type === PRICING_COLUMN_TYPES.TOTAL) {
      const valueType = getValueType(column);
      updatedSummaryColumns[index] = {
        ...column,
        value: formatCellValue(total, valueType),
      };
    }
  });

  return updatedSummaryColumns;
};

/**
 * Handles changing a cell value in a pricing table column
 *
 * @param {Array} columns - The current columns array
 * @param {number} columnIndex - The index of the column being changed
 * @param {number} rowIndex - The index of the row being changed
 * @param {any} newValue - The new value for the cell
 * @returns {Array} Updated columns array with the new cell value
 *
 * @example
 * // Update cell value in the first column, second row to 100
 * const updatedColumns = handleCellChange(columns, 0, 1, 100);
 */
export const handleCellChange = (columns, columnIndex, rowIndex, newValue) => {
  const updatedColumns = [...columns];
  const cell = updatedColumns[columnIndex].cells[rowIndex];

  // Update the cell value while preserving other properties
  updatedColumns[columnIndex].cells[rowIndex] = {
    ...cell,
    value: newValue,
  };

  // If this is not a subtotal column, we need to update any subtotal columns in this row
  const changedColumn = updatedColumns[columnIndex];
  if (changedColumn.type !== PRICING_COLUMN_TYPES.SUBTOTAL) {
    // Find all subtotal columns
    const subtotalColumnIndices = updatedColumns
      .map((col, index) => (col.type === PRICING_COLUMN_TYPES.SUBTOTAL ? index : -1))
      .filter((index) => index !== -1);

    // For each subtotal column, recalculate and update the value for this row
    subtotalColumnIndices.forEach((subtotalIndex) => {
      if (updatedColumns[subtotalIndex].cells[rowIndex]) {
        // Calculate the subtotal for this row
        const subtotal = calculateRowSubtotal(updatedColumns, rowIndex);
        const valueType = getValueType(updatedColumns[subtotalIndex]);
        const formattedSubtotal = formatCellValue(subtotal, valueType);

        // Update the subtotal cell with the calculated value
        updatedColumns[subtotalIndex].cells[rowIndex] = {
          ...updatedColumns[subtotalIndex].cells[rowIndex],
          value: formattedSubtotal,
          _lastUpdated: Date.now() // Add a timestamp to force re-render
        };
      }
    });
  }

  return updatedColumns;
};

/**
 * Handles changing a summary cell value
 *
 * @param {Array} summaryColumns - The current summary columns array
 * @param {number} columnIndex - The index of the summary column being changed
 * @param {any} newValue - The new value for the summary cell
 * @returns {Array} Updated summary columns array with the new value
 *
 * @example
 * // Update the value of the first summary column to 500
 * const updatedSummaryColumns = handleSummaryCellChange(summaryColumns, 0, 500);
 */
export const handleSummaryCellChange = (summaryColumns, columnIndex, newValue, columns = []) => {
  if (!summaryColumns || columnIndex < 0 || columnIndex >= summaryColumns.length) {
    return summaryColumns;
  }

  const updatedSummaryColumns = [...summaryColumns];

  // Update the cell value
  updatedSummaryColumns[columnIndex] = {
    ...updatedSummaryColumns[columnIndex],
    value: newValue,
  };

  // Update SUBTOTAL and TOTAL values
  const subtotal = calculateSubtotal(columns);

  // Update all SUBTOTAL columns
  updatedSummaryColumns.forEach((column, index) => {
    if (column.type === PRICING_COLUMN_TYPES.SUBTOTAL) {
      const valueType = getValueType(column);
      updatedSummaryColumns[index] = {
        ...column,
        value: formatCellValue(subtotal, valueType),
      };
    }
  });

  // Update all TOTAL columns
  const total = calculateTotal(subtotal, updatedSummaryColumns);
  updatedSummaryColumns.forEach((column, index) => {
    if (column.type === PRICING_COLUMN_TYPES.TOTAL) {
      const valueType = getValueType(column);
      updatedSummaryColumns[index] = {
        ...column,
        value: formatCellValue(total, valueType),
      };
    }
  });

  return updatedSummaryColumns;
};

/**
 * Handles moving a column left or right
 *
 * @param {Array} columns - The current columns array
 * @param {number} columnIndex - The index of the column to move
 * @param {string} direction - The direction to move the column ('left' or 'right')
 * @returns {Array} Updated columns array with the column moved to the new position
 *
 * @example
 * // Move the second column to the right
 * const updatedColumns = handleMoveColumn(columns, 1, 'right');
 */
export const handleMoveColumn = (columns, columnIndex, direction) => {
  const updatedColumns = [...columns];
  const targetIndex = direction === 'left' ? columnIndex - 1 : columnIndex + 1;

  // Check if the target index is valid
  if (targetIndex < 0 || targetIndex >= updatedColumns.length) {
    return columns; // Return original columns if move is invalid
  }

  // Swap the columns
  [updatedColumns[columnIndex], updatedColumns[targetIndex]] =
  [updatedColumns[targetIndex], updatedColumns[columnIndex]];

  return updatedColumns;
};

/**
 * Handles toggling the visibility of a column
 *
 * @param {Array} columns - The current columns array
 * @param {number} columnIndex - The index of the column to toggle visibility
 * @returns {Array} Updated columns array with the column's visibility toggled
 *
 * @example
 * // Hide or show the third column
 * const updatedColumns = handleToggleColumnVisibility(columns, 2);
 */
export const handleToggleColumnVisibility = (columns, columnIndex, summaryColumns = []) => {
  const updatedColumns = [...columns];

  // Toggle the hidden property
  updatedColumns[columnIndex] = {
    ...updatedColumns[columnIndex],
    hidden: !updatedColumns[columnIndex].hidden
  };

  const updatedSummaryColumns = updateSummaryCalculatedValues(updatedColumns, summaryColumns);

  return {
    columns: updatedColumns,
    summaryColumns: updatedSummaryColumns
  };
};

/**
 * Handles inserting a new column
 *
 * @param {Array} columns - The current columns array
 * @param {number} columnIndex - The index after which to insert the new column
 * @param {string} columnType - The type of column to insert
 * @param {number} currentRowCount - The current number of rows in the table
 * @param {Array} summaryColumns - The current summary columns array
 * @returns {Object} Object containing updated columns and summary columns
 *
 * @example
 * // Insert a new price column after the first column
 * const { columns: updatedColumns, summaryColumns: updatedSummaryColumns } =
 *   handleInsertColumn(columns, 0, 'price', 5, summaryColumns);
 */
export const handleInsertColumn = (columns, columnIndex, columnType, currentRowCount, summaryColumns = []) => {
  const updatedColumns = [...columns];
  const defaultRowValues = PRICING_COLUMN_CELL_DEFAULT_ROW_VALUES[columnType];

  const columnTypeLabel = columnType.charAt(0).toUpperCase() + columnType.slice(1);
  const newColumn = {
    _id: generateObjectId(),
    label: columnTypeLabel,
    type: columnType,
    hidden: false,
    cells: Array(currentRowCount).fill().map(() => ({
      value: defaultRowValues.value
    }))
  };

  updatedColumns.splice(columnIndex + 1, 0, newColumn);

  const updatedSummaryColumns = updateSummaryCalculatedValues(updatedColumns, summaryColumns);

  return {
    columns: updatedColumns,
    summaryColumns: updatedSummaryColumns
  };
};

/**
 * Handles inserting a new summary column
 *
 * @param {Array} summaryColumns - The current summary columns array
 * @param {string} columnType - The type of summary column to insert
 * @param {number} position - The position at which to insert the new column (-1 to append at the end)
 * @param {Array} columns - The regular columns array (needed for SUBTOTAL and TOTAL calculations)
 * @returns {Array} Updated summary columns array with the new summary column inserted
 *
 * @example
 * // Add a new total summary column at the end
 * const updatedSummaryColumns = handleInsertSummaryColumn(summaryColumns, 'total', -1);
 */
export const handleInsertSummaryColumn = (summaryColumns, columnType, position = -1, columns = []) => {
  const updatedSummaryColumns = [...summaryColumns];

  // Get default format based on column type
  let format = 'percent';
  if (columnType === PRICING_COLUMN_TYPES.TOTAL || columnType === PRICING_COLUMN_TYPES.SUBTOTAL) {
    format = 'amount';
  }

  // For SUBTOTAL and TOTAL columns, calculate the value instead of using a default
  let value;
  if (columnType === PRICING_COLUMN_TYPES.SUBTOTAL && columns.length > 0) {
    const subtotal = calculateSubtotal(columns);
    value = formatCellValue(subtotal, 'amount');
  } else if (columnType === PRICING_COLUMN_TYPES.TOTAL && columns.length > 0) {
    const subtotal = calculateSubtotal(columns);
    const total = calculateTotal(subtotal, updatedSummaryColumns);
    value = formatCellValue(total, 'amount');
  } else {
    value = getDefaultValueForColumnType(columnType, format);
  }

  const columnTypeLabel = columnType.charAt(0).toUpperCase() + columnType.slice(1);

  const newSummaryColumn = {
    _id: generateObjectId(),
    label: columnTypeLabel,
    type: columnType,
    value,
    format
  };

  if (position >= 0 && position <= updatedSummaryColumns.length) {
    updatedSummaryColumns.splice(position, 0, newSummaryColumn);
  } else {
    updatedSummaryColumns.push(newSummaryColumn);
  }

  return updatedSummaryColumns;
};

/**
 * Handles changing a column's type
 *
 * @param {Array} columns - The current columns array
 * @param {number} columnIndex - The index of the column to change
 * @param {string} newValueType - The new value type for the column
 * @param {Array} summaryColumns - The current summary columns array
 * @returns {Object} Object containing updated columns and summary columns
 *
 * @example
 * // Change the second column to a discount type
 * const { columns: updatedColumns, summaryColumns: updatedSummaryColumns } =
 *   handleChangeColumnType(columns, 1, 'discount', summaryColumns);
 */
export const handleChangeColumnType = (columns, columnIndex, newValueType, summaryColumns = []) => {
  const updatedColumns = [...columns];
  const column = updatedColumns[columnIndex];

  updatedColumns[columnIndex] = {
    ...column,
    valueType: newValueType,
    // Update the cells to match the new value type format
    cells: column.cells.map((cell) => {
      const formattedValue = formatCellValue(
        parseDisplayValue(cell.value, getValueType(column)),
        newValueType
      );

      return {
        ...cell,
        value: formattedValue,
      };
    })
  };

  const updatedSummaryColumns = updateSummaryCalculatedValues(updatedColumns, summaryColumns);

  return {
    columns: updatedColumns,
    summaryColumns: updatedSummaryColumns
  };
};

/**
 * Handles changing a column's format
 *
 * @param {Array} columns - The current columns array
 * @param {number} columnIndex - The index of the column to change
 * @param {string} newFormat - The new format for the column
 * @param {Array} summaryColumns - The current summary columns array
 * @returns {Object} Object containing updated columns and summary columns
 *
 * @example
 * // Change the third column's format to percent
 * const { columns: updatedColumns, summaryColumns: updatedSummaryColumns } =
 *   handleChangeColumnFormat(columns, 2, 'percent', summaryColumns);
 */
export const handleChangeColumnFormat = (columns, columnIndex, newFormat, summaryColumns = []) => {
  const updatedColumns = [...columns];
  const column = updatedColumns[columnIndex];

  updatedColumns[columnIndex] = {
    ...column,
    format: newFormat
  };

  updatedColumns[columnIndex].cells = column.cells.map((cell) => {
    const newValue = applyFormatChange(
      cell.value,
      getValueType(column),
      getValueType({ ...column, format: newFormat })
    );

    return {
      ...cell,
      value: newValue,
    };
  });

  const updatedSummaryColumns = updateSummaryCalculatedValues(updatedColumns, summaryColumns);

  return {
    columns: updatedColumns,
    summaryColumns: updatedSummaryColumns
  };
};

/**
 * Handles changing a summary column's format
 *
 * @param {Array} summaryColumns - The current summary columns array
 * @param {string} columnId - The ID of the summary column to change
 * @param {string} newFormat - The new format for the summary column
 * @param {Array} columns - The regular columns array for recalculating
 * @returns {Array} Updated summary columns array with the column's format changed
 *
 * @example
 * // Change a summary column's format to amount
 * const updatedSummaryColumns = handleChangeSummaryColumnFormat(summaryColumns, 'col123', 'amount', columns);
 */
export const handleChangeSummaryColumnFormat = (summaryColumns, columnId, newFormat, columns = []) => {
  const updatedSummaryColumns = [...summaryColumns];
  const columnIndex = updatedSummaryColumns.findIndex((col) => col._id === columnId);

  if (columnIndex !== -1) {
    const column = updatedSummaryColumns[columnIndex];

    // Apply the format change to the column value
    const newValue = applyFormatChange(
      column.value,
      getValueType(column),
      getValueType({ ...column, format: newFormat })
    );

    updatedSummaryColumns[columnIndex] = {
      ...column,
      format: newFormat,
      value: newValue,
    };

    // Recalculate TOTAL values after a summary column format change
    // First get the subtotal
    const subtotal = calculateSubtotal(columns);

    // Update TOTAL columns since summary values might have changed
    const total = calculateTotal(subtotal, updatedSummaryColumns);
    updatedSummaryColumns.forEach((col, idx) => {
      if (col.type === PRICING_COLUMN_TYPES.TOTAL) {
        const valueType = getValueType(col);
        updatedSummaryColumns[idx] = {
          ...col,
          value: formatCellValue(total, valueType),
        };
      }
    });

    return updatedSummaryColumns;
  }

  return summaryColumns; // Return original if column not found
};

/**
 * Handles deleting a column from the pricing table
 *
 * @param {Array} columns - The current columns array
 * @param {number} columnIndex - The index of the column to delete
 * @returns {Array} Updated columns array with the column removed, or original array if deletion not allowed
 *
 * @example
 * // Delete the fourth column
 * const updatedColumns = handleDeleteColumn(columns, 3);
 */
export const handleDeleteColumn = (columns, columnIndex, summaryColumns = []) => {
  if (columns.length <= 1) {
    return {
      columns,
      summaryColumns
    };
  }

  const updatedColumns = [...columns];
  updatedColumns.splice(columnIndex, 1);

  const updatedSummaryColumns = updateSummaryCalculatedValues(updatedColumns, summaryColumns);

  return {
    columns: updatedColumns,
    summaryColumns: updatedSummaryColumns
  };
};

/**
 * Handles deleting a summary column from the pricing table
 *
 * @param {Array} summaryColumns - The current summary columns array
 * @param {string} columnId - The ID of the summary column to delete
 * @returns {Array} Updated summary columns array with the column removed
 *
 * @example
 * // Delete a summary column with ID 'col456'
 * const updatedSummaryColumns = handleDeleteSummaryColumn(summaryColumns, 'col456');
 */
export const handleDeleteSummaryColumn = (summaryColumns, columnId) => {
  return summaryColumns.filter((column) => column._id !== columnId);
};

/**
 * Handles showing a hidden column
 *
 * @param {Array} columns - The current columns array
 * @param {string} columnId - The ID of the column to show
 * @returns {Array} Updated columns array with the column now visible
 *
 * @example
 * // Show a hidden column with ID 'col789'
 * const updatedColumns = showColumn(columns, 'col789');
 */
export const showColumn = (columns, columnId, summaryColumns = []) => {
  const updatedColumns = [...columns];
  const columnIndex = updatedColumns.findIndex((col) => col._id === columnId);

  if (columnIndex !== -1) {
    updatedColumns[columnIndex] = {
      ...updatedColumns[columnIndex],
      hidden: false
    };

    const updatedSummaryColumns = updateSummaryCalculatedValues(updatedColumns, summaryColumns);

    return {
      columns: updatedColumns,
      summaryColumns: updatedSummaryColumns
    };
  }

  return {
    columns,
    summaryColumns
  };
};

/**
 * Creates menu items for the Hidden Columns dropdown
 *
 * @param {Array} hiddenColumns - Array of hidden columns
 * @param {Function} showColumnFn - Function to call when showing a column
 * @returns {Array} Array of menu item objects for the dropdown
 *
 * @example
 * // Get menu items for hidden columns dropdown
 * const menuItems = getHiddenColumnsMenuItems(hiddenColumns, (id) => showColumn(columns, id));
 */
export const getHiddenColumnsMenuItems = (hiddenColumns, showColumnFn) => {
  return hiddenColumns.map((column) => ({
    id: `show-${column._id}`,
    label: column.label,
    onClick: () => showColumnFn(column._id),
    rightElement: 'Show'
  }));
};

/**
 * Handles inserting a new row after a specified row index
 *
 * @param {Array} columns - The current columns array
 * @param {number} rowIndex - The index after which to insert the new row
 * @returns {Array} Updated columns array with the new row inserted
 *
 * @example
 * // Insert a new row after the second row
 * const updatedColumns = handleInsertRowBelow(columns, 1);
 */
export const handleInsertRowBelow = (columns, rowIndex, summaryColumns = []) => {
  const updatedColumns = columns.map((column) => {
    const defaultValue = column.type === PRICING_COLUMN_TYPES.DESCRIPTION ? 'Item' : '0';
    const newCell = {
      value: defaultValue,
    };

    // Create a new cells array with the new cell inserted at rowIndex + 1
    const cells = [
      ...column.cells.slice(0, rowIndex + 1),
      newCell,
      ...column.cells.slice(rowIndex + 1)
    ];

    return { ...column, cells };
  });

  const updatedSummaryColumns = updateSummaryCalculatedValues(updatedColumns, summaryColumns);

  return {
    columns: updatedColumns,
    summaryColumns: updatedSummaryColumns
  };
};

/**
 * Handles deleting a row from the table
 *
 * @param {Array} columns - The current columns array
 * @param {number} rowIndex - The index of the row to delete
 * @returns {Array} Updated columns array with the row removed
 *
 * @example
 * // Delete the third row
 * const updatedColumns = handleDeleteRow(columns, 2);
 */
export const handleDeleteRow = (columns, rowIndex, summaryColumns = []) => {
  // Don't allow deleting the last row
  const rowCount = columns.length > 0 ? columns[0].cells.length : 0;
  if (rowCount <= 1) {
    return {
      columns,
      summaryColumns
    };
  }

  const updatedColumns = [...columns];

  // Remove the cell at rowIndex from each column
  updatedColumns.forEach((column, columnIndex) => {
    const updatedCells = [...column.cells];
    updatedCells.splice(rowIndex, 1);

    // Create completely new column object with new cell objects to ensure proper re-rendering
    updatedColumns[columnIndex] = {
      ...column,
      cells: updatedCells.map((cell) => ({ ...cell })) // Create all new cell objects
    };
  });

  const updatedSummaryColumns = updateSummaryCalculatedValues(updatedColumns, summaryColumns);

  return {
    columns: updatedColumns,
    summaryColumns: updatedSummaryColumns
  };
};
